import React, { useState, useMemo, useCallback } from "react";
import { GoogleMap, useJsApiLoader, MarkerF, InfoWindowF } from "@react-google-maps/api";
import { motion } from "framer-motion";
import { useApp } from "@/context/AppContext";
import { GOOGLE_MAPS_LOADER, GOOGLE_MAPS_KEY } from "@/lib/maps";
import { MapPin, Car, Footprints, Bus, Compass, Gauge } from "lucide-react";

const PROFILE_META = {
  tourism: { color: "#0ea5e9" }, business: { color: "#6366f1" }, balneare: { color: "#06b6d4" },
  aeroportuale: { color: "#8b5cf6" }, universitaria: { color: "#f59e0b" }, sanitaria: { color: "#ef4444" },
  eventi: { color: "#ec4899" }, crociere: { color: "#14b8a6" },
};

const CAT_COLOR = {
  airport: "#8b5cf6", train_station: "#6366f1", subway: "#6366f1", bus: "#94a3b8",
  port: "#14b8a6", cruise: "#14b8a6", beach: "#06b6d4", historic_center: "#f59e0b",
  museum: "#f59e0b", monument: "#f59e0b", attraction: "#0ea5e9", university: "#f59e0b",
  hospital: "#ef4444", clinic: "#ef4444", convention: "#ec4899", expo: "#ec4899",
  stadium: "#ec4899", amusement: "#0ea5e9", mall: "#64748b", business_district: "#6366f1",
};

const mapStyle = { width: "100%", height: "340px", borderRadius: "10px" };

export default function GeoContextPanel({ propId, prop }) {
  const { t, lang } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [map, setMap] = useState(null);

  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_LOADER);

  React.useEffect(() => {
    let alive = true;
    setLoading(true);
    import("@/lib/api").then(({ api }) =>
      api.get(`/properties/${propId}/geo-context`).then(({ data }) => { if (alive) setData(data); })
        .catch(() => {}).finally(() => { if (alive) setLoading(false); })
    );
    return () => { alive = false; };
  }, [propId]);

  const center = useMemo(() => ({
    lat: prop?.latitude || 41.9, lng: prop?.longitude || 12.5,
  }), [prop]);

  const points = data?.points || [];
  const profile = data?.profile || {};
  const dist = profile.distribution || {};
  const labels = profile.labels || {};
  const sorted = Object.keys(dist).sort((a, b) => (dist[b] || 0) - (dist[a] || 0));
  const dominant = profile.dominant;

  const onLoad = useCallback((m) => setMap(m), []);
  React.useEffect(() => {
    if (map && points.length && window.google) {
      const b = new window.google.maps.LatLngBounds();
      b.extend(center);
      points.forEach((p) => b.extend({ lat: p.lat, lng: p.lon }));
      map.fitBounds(b, 60);
    }
  }, [map, points, center]);

  const lbl = (k) => (labels[k] ? labels[k][lang === "it" ? "it" : "en"] : k);
  const tm = (obj) => (obj && obj.minutes != null ? `${obj.minutes} ${t("minShort")}` : "—");

  return (
    <motion.div
      data-testid="geo-context-panel"
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
      className="md:col-span-4 bg-card border border-border rounded-lg p-6"
    >
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
          <Compass className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h3 className="text-lg font-semibold leading-tight">{t("structureContext")}</h3>
          <p className="text-sm text-muted-foreground">{t("contextSubtitle")}</p>
        </div>
        {dominant && (
          <span className="ml-auto text-xs font-semibold px-3 py-1.5 rounded-full flex items-center gap-1.5"
            style={{ backgroundColor: `color-mix(in srgb, ${PROFILE_META[dominant]?.color || "var(--primary)"} 15%, transparent)`, color: PROFILE_META[dominant]?.color }}>
            <Gauge className="w-3.5 h-3.5" />{t("dominantVocation")}: {lbl(dominant)}
          </span>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground py-16 text-center flex items-center justify-center gap-2">
          <Compass className="w-4 h-4 animate-pulse text-primary" /> {t("analyzing")}
        </p>
      ) : (data?.status !== "ready") ? (
        <p className="text-sm text-muted-foreground py-12 text-center" data-testid="geo-unavailable">{t("geoUnavailable")}</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Map */}
          <div className="lg:col-span-2">
            {isLoaded && GOOGLE_MAPS_KEY ? (
              <GoogleMap mapContainerStyle={mapStyle} center={center} zoom={13} onLoad={onLoad}
                options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: true }}>
                <MarkerF position={center} icon={window.google ? {
                  path: window.google.maps.SymbolPath.CIRCLE, scale: 9,
                  fillColor: "#e11d48", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 3,
                } : undefined} title={prop?.name} />
                {points.map((p, i) => (
                  <MarkerF key={i} position={{ lat: p.lat, lng: p.lon }} onClick={() => setActive(i)}
                    icon={window.google ? {
                      path: window.google.maps.SymbolPath.CIRCLE, scale: 6,
                      fillColor: CAT_COLOR[p.key] || "#64748b", fillOpacity: 0.95, strokeColor: "#fff", strokeWeight: 1.5,
                    } : undefined} />
                ))}
                {active != null && points[active] && (
                  <InfoWindowF position={{ lat: points[active].lat, lng: points[active].lon }} onCloseClick={() => setActive(null)}>
                    <div style={{ minWidth: 150 }}>
                      <p style={{ fontWeight: 700, fontSize: 13, margin: 0 }}>{points[active].name}</p>
                      <p style={{ fontSize: 11, color: "#666", margin: "2px 0" }}>{points[active].category_it} · {points[active].distance_km} km</p>
                      <p style={{ fontSize: 11, margin: 0 }}>🚗 {tm(points[active].drive)} · 🚶 {tm(points[active].walk)} · 🚌 {tm(points[active].transit)}</p>
                    </div>
                  </InfoWindowF>
                )}
              </GoogleMap>
            ) : (
              <div className="flex items-center justify-center bg-secondary/40 rounded-lg" style={{ height: 340 }}>
                <p className="text-sm text-muted-foreground">{t("mapUnavailable")}</p>
              </div>
            )}

            {/* Points of interest list */}
            <div className="mt-4">
              <p className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground mb-2">{t("pointsOfInterest")} ({points.length})</p>
              <div className="max-h-64 overflow-y-auto space-y-1.5 pr-1" data-testid="geo-points-list">
                {points.map((p, i) => (
                  <button key={i} data-testid={`geo-point-${i}`} onClick={() => setActive(i)}
                    className="w-full flex items-center gap-3 text-left border border-border rounded-md px-3 py-2 hover:border-primary/40 hover:bg-accent/40 transition-colors">
                    <MapPin className="w-4 h-4 shrink-0" style={{ color: CAT_COLOR[p.key] || "#64748b" }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{p.name}</p>
                      <p className="text-[11px] text-muted-foreground">{p.category_it} · <span className="font-data">{p.distance_km}</span> km</p>
                    </div>
                    <div className="flex items-center gap-2.5 text-[11px] text-muted-foreground shrink-0">
                      <span className="inline-flex items-center gap-1" title={t("travelCar")}><Car className="w-3.5 h-3.5" />{tm(p.drive)}</span>
                      <span className="inline-flex items-center gap-1" title={t("travelWalk")}><Footprints className="w-3.5 h-3.5" />{tm(p.walk)}</span>
                      <span className="inline-flex items-center gap-1" title={t("travelTransit")}><Bus className="w-3.5 h-3.5" />{tm(p.transit)}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Profile bars */}
          <div>
            <p className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground mb-1">{t("structureProfile")}</p>
            <p className="text-[11px] text-muted-foreground mb-3">{lang === "it" ? "Quota % sul mix di vocazioni della zona (somma 100%)." : "% share of the area's vocation mix (sums to 100%)."}</p>
            <div className="space-y-3" data-testid="geo-profile">
              {sorted.map((k) => {
                const v = dist[k] || 0;
                const color = PROFILE_META[k]?.color || "var(--primary)";
                return (
                  <div key={k} data-testid={`profile-${k}`}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="font-medium">{lbl(k)}</span>
                      <span className="font-data font-semibold" style={{ color }}>{v}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-secondary overflow-hidden">
                      <motion.div initial={{ width: 0 }} animate={{ width: `${v}%` }} transition={{ duration: 0.6 }}
                        className="h-full rounded-full" style={{ backgroundColor: color }} />
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground mt-4 flex items-start gap-1.5">
              <Gauge className="w-3.5 h-3.5 mt-0.5 text-primary shrink-0" /> {t("contextInPricing")}
            </p>
          </div>
        </div>
      )}
    </motion.div>
  );
}
